<template>
  <div>
    <b-alert
      :show="!!alertFilter"
      variant="primary"
      class="d-flex align-items-center justify-content-between"
    >
      <span>
        {{ $t('Exibindo apenas vendas com o alerta') }} "{{ alertFilterLabel }}".
        <span v-if="alertSalesOutsidePeriod">
          {{ $t('Vendas afetadas fora do período filtrado') }}: {{ alertSalesOutsidePeriod }}.
        </span>
      </span>
      <e-button
        id="auto-delivery-monitor_clear-alert"
        size="sm"
        variant="outline-primary"
        icon="x"
        :text="$t('Limpar')"
        @click="$emit('clear-alert')"
      />
    </b-alert>

    <b-row>
      <b-col md="3">
        <FormulateInput
          id="auto-delivery-monitor_sale-filter"
          v-model="localFilters.saleId"
          type="text"
          :label="$t('Venda')"
          :placeholder="$t('Número da venda')"
        />
      </b-col>
      <b-col md="3">
        <FormulateInput
          id="auto-delivery-monitor_auto-state-filter"
          v-model="localFilters.autoState"
          type="vue-select"
          :label="$t('Estado automático')"
          :placeholder="$t('Todos')"
          :options="options(autoDeliveryStateLabels)"
        />
      </b-col>
      <b-col md="2">
        <FormulateInput
          id="auto-delivery-monitor_work-state-filter"
          v-model="localFilters.workState"
          type="vue-select"
          :label="$t('Estado do work')"
          :placeholder="$t('Todos')"
          :options="options(workStateLabels)"
        />
      </b-col>
      <b-col md="2">
        <FormulateInput
          id="auto-delivery-monitor_supplier-filter"
          v-model="localFilters.supplierId"
          type="vue-select"
          :label="$t('Fornecedor')"
          :placeholder="$t('Todos')"
          :options="supplierOptions"
        />
      </b-col>
      <b-col
        md="2"
        class="d-flex align-items-center"
      >
        <FormulateInput
          id="auto-delivery-monitor_only-alerts-filter"
          v-model="localFilters.onlyAlerts"
          type="checkbox"
          :label="$t('Somente com alerta')"
        />
      </b-col>
    </b-row>

    <b-table
      show-empty
      responsive
      striped
      small
      class="bordered"
      :empty-text="$t('Nenhuma venda passou pela entrega automática no período e lojas filtrados.')"
      :fields="fields"
      :items="filteredSales"
      :per-page="perPage"
      :current-page="currentPage"
      primary-key="saleId"
    >
      <template #cell(saleId)="{ item }">
        <b-link
          :id="`auto-delivery-monitor_open-${item.saleId}`"
          @click="$emit('open', item.saleId)"
        >
          #{{ item.saleId }}
        </b-link>
      </template>
      <template #cell(createdDate)="{ item }">
        {{ formatDateTime(item.createdDate) }}
      </template>
      <template #cell(autoDeliveryState)="{ item }">
        <b-badge :variant="variant(autoDeliveryStateLabels, item.autoDeliveryState)">
          {{ $t(label(autoDeliveryStateLabels, item.autoDeliveryState)) }}
        </b-badge>
        <small
          v-if="item.ineligibilityReason"
          class="d-block text-muted"
        >
          {{ $t(label(ineligibilityReasonLabels, item.ineligibilityReason)) }}
        </small>
      </template>
      <template #cell(workState)="{ item }">
        <b-badge
          v-if="item.workState"
          :variant="variant(workStateLabels, item.workState)"
        >
          {{ $t(label(workStateLabels, item.workState)) }}
        </b-badge>
        <small class="d-block text-muted">
          {{ item.workAttemptCount }} {{ $t('tentativa(s)') }}
        </small>
        <small
          v-if="item.workLastErrorCode"
          class="d-block text-danger"
        >
          {{ $t(item.workLastErrorCode) }}
        </small>
      </template>
      <template #cell(dispatches)="{ item }">
        <span v-if="!item.dispatches.length">-</span>
        <b-badge
          v-for="dispatch in item.dispatches"
          :key="dispatch.id"
          :variant="dispatch.isAccepted ? 'success' : variant(dispatchStateLabels, dispatch.executionState)"
          class="mr-25 mb-25"
          :title="dispatch.supplierStatus || ''"
        >
          {{ dispatch.deliverySupplierName || `#${dispatch.deliverySupplierId}` }}:
          {{ $t(label(dispatchStateLabels, dispatch.executionState)) }}
        </b-badge>
      </template>
      <template #cell(motoboyName)="{ item }">
        {{ item.motoboyName || '-' }}
      </template>
      <template #cell(deliveryStatus)="{ item }">
        {{ $t(label(deliveryStatusLabels, item.deliveryStatus)) }}
      </template>
      <template #cell(age)="{ item }">
        {{ isInProgress(item) ? formatAge(item.enteredPipelineAt) : '-' }}
      </template>
      <template #cell(alerts)="{ item }">
        <b-badge
          v-for="alert in item.alerts"
          :key="alert"
          variant="light-danger"
          class="mr-25 mb-25"
        >
          {{ $t(label(alertLabels, alert)) }}
        </b-badge>
      </template>
    </b-table>

    <div class="d-flex align-items-center justify-content-between">
      <small class="text-muted">
        {{ filteredSales.length }} {{ $t('de') }} {{ sales.length }} {{ $t('venda(s)') }}
      </small>
      <b-pagination
        v-if="filteredSales.length > perPage"
        v-model="currentPage"
        :total-rows="filteredSales.length"
        :per-page="perPage"
        class="mb-0"
      />
    </div>
  </div>
</template>

<script>
import { BAlert, BBadge, BCol, BLink, BPagination, BRow, BTable } from 'bootstrap-vue'
import { EButton } from '@/views/components'
import {
  alertLabels,
  autoDeliveryStateLabels,
  deliveryStatusLabels,
  dispatchStateLabels,
  formatAge,
  formatDateTime,
  ineligibilityReasonLabels,
  label,
  variant,
  workStateLabels,
} from '../monitor-labels'

const inProgressWorkStates = ['Scheduled', 'Queued', 'Processing', 'RetryPending']

export default {
  name: 'MonitorSalesTable',

  components: { BAlert, BBadge, BCol, BLink, BPagination, BRow, BTable, EButton },

  props: {
    sales: {
      type: Array,
      default: () => [],
    },
    // { alert, saleIds } vindo do bloco de saúde.
    alertFilter: {
      type: Object,
      default: null,
    },
  },

  data() {
    return {
      localFilters: {
        saleId: '',
        autoState: null,
        workState: null,
        supplierId: null,
        onlyAlerts: false,
      },
      currentPage: 1,
      perPage: 50,
      alertLabels,
      autoDeliveryStateLabels,
      deliveryStatusLabels,
      dispatchStateLabels,
      ineligibilityReasonLabels,
      workStateLabels,
    }
  },

  computed: {
    fields() {
      return [
        { key: 'saleId', label: this.$t('Venda') },
        { key: 'storeName', label: this.$t('Loja') },
        { key: 'createdDate', label: this.$t('Criada em') },
        { key: 'autoDeliveryState', label: this.$t('Estado automático') },
        { key: 'workState', label: this.$t('Work') },
        { key: 'dispatches', label: this.$t('Fornecedores') },
        { key: 'motoboyName', label: this.$t('Motoboy próprio') },
        { key: 'deliveryStatus', label: this.$t('Entrega') },
        { key: 'age', label: this.$t('Idade') },
        { key: 'alerts', label: this.$t('Alertas') },
      ]
    },
    supplierOptions() {
      const suppliers = new Map()
      this.sales.forEach(sale =>
        sale.dispatches.forEach(dispatch =>
          suppliers.set(
            dispatch.deliverySupplierId,
            dispatch.deliverySupplierName || `#${dispatch.deliverySupplierId}`
          )
        )
      )
      return [...suppliers.entries()].map(([value, text]) => ({ value, label: text }))
    },
    alertSaleIds() {
      return this.alertFilter ? new Set(this.alertFilter.saleIds.map(String)) : null
    },
    alertFilterLabel() {
      return this.alertFilter ? this.$t(label(alertLabels, this.alertFilter.alert)) : ''
    },
    alertSalesOutsidePeriod() {
      if (!this.alertSaleIds) return 0
      const loaded = new Set(this.sales.map(sale => String(sale.saleId)))
      return [...this.alertSaleIds].filter(id => !loaded.has(id)).length
    },
    filteredSales() {
      const { saleId, autoState, workState, supplierId, onlyAlerts } = this.localFilters
      const saleTerm = String(saleId || '').replace(/\D/g, '')
      return this.sales.filter(
        sale =>
          (!this.alertSaleIds || this.alertSaleIds.has(String(sale.saleId))) &&
          (!saleTerm || String(sale.saleId).includes(saleTerm)) &&
          (!autoState || sale.autoDeliveryState === autoState) &&
          (!workState || sale.workState === workState) &&
          (!supplierId ||
            sale.dispatches.some(dispatch => dispatch.deliverySupplierId === supplierId)) &&
          (!onlyAlerts || sale.alerts.length > 0)
      )
    },
  },

  watch: {
    filteredSales() {
      const lastPage = Math.max(1, Math.ceil(this.filteredSales.length / this.perPage))
      if (this.currentPage > lastPage) this.currentPage = lastPage
    },
  },

  methods: {
    label,
    variant,
    formatAge,
    formatDateTime,
    options(map) {
      return Object.entries(map).map(([value, meta]) => ({ value, label: this.$t(meta.text) }))
    },
    isInProgress(sale) {
      return inProgressWorkStates.includes(sale.workState)
    },
  },
}
</script>
